import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { getImageURL } from 'volto-ministerio-publico-federal/helpers/Image/Image';
import { getYoutubeVideoId } from 'volto-ministerio-publico-federal/helpers/Utils/Utils';
import {
  resolveSocialNetwork,
  type SocialNetworkToken,
} from 'volto-ministerio-publico-federal/helpers/Social/Social';
import {
  SOCIAL_LABELS,
  SOCIAL_ORDER,
  type SocialGlyphToken,
} from 'volto-ministerio-publico-federal/components/Icons/social';

/**
 * Tipos e utilitários da view da Capa (Home).
 *
 * As notícias vêm do endpoint REST `@search` (`portal_type=News`), com
 * `tema`/`unit_title` expostos como metadata.
 *
 * Os slots legados `du1..du3`/`ef1..ef3` do behavior `capa_destaques` não têm
 * mais consumidor no frontend — a curadoria agora vive no bloco
 * `mpfDestaques`. O campo continua no backend, com os dados preservados.
 */

/** Item de notícia retornado pelo `@search`. */
export interface NewsItem {
  '@id': string;
  title?: string;
  description?: string;
  tema?: string;
  unit_title?: string;
  effective?: string;
  // `any` para compatibilizar com os props `item` de Image/ConditionalLink do Volto.
  image_scales?: any;
  [key: string]: any;
}

const TIME_ZONE = 'America/Sao_Paulo';

/**
 * Formata a data de publicação no padrão da capa (`23/06/2026 | 15:19`).
 * Retorna `null` para valores ausentes/inválidos.
 */
export const formatNewsDate = (
  value?: string,
): { iso: string; label: string } | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString('pt-BR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString('pt-BR', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  });
  return { iso: date.toISOString(), label: `${day} | ${time}` };
};

/* -----------------------------------------------------------------------
   Faixa de destaques da capa (bloco `mpfDestaques`)
   ----------------------------------------------------------------------- */

/** Formato de cada posição da grade: manchete, card alto e cards da 2ª linha. */
export type DestaqueVariant = 'hero' | 'tall' | 'card';

/** Referência do `object_browser` (sempre uma lista, mesmo com um item só). */
export interface BrowserRef {
  '@id': string;
  '@type'?: string;
  title?: string;
  [key: string]: unknown;
}

/** Item curado no bloco `mpfDestaques` (widget `object_list`). */
export interface DestaqueItem {
  title?: string;
  description?: string;
  alt?: string;
  isVideo?: boolean;
  external?: boolean;
  variant?: DestaqueVariant;
  href?: BrowserRef[];
  /**
   * Widget `image`: grava a URL do conteúdo (upload/escolha no site) ou uma
   * URL externa colada. Itens antigos, do tempo do `object_browser`, ainda
   * podem trazer uma lista de referências.
   */
  image?: string | BrowserRef[];
  /** Campo aposentado; itens antigos ainda podem trazê-lo preenchido. */
  imageUrl?: string;
}

/** Dados do bloco `mpfDestaques`. */
export interface DestaquesData {
  items?: DestaqueItem[];
}

/**
 * View-model único da grade: normaliza item curado e notícia do `@search` num
 * mesmo formato, para os cards não saberem de onde o destaque veio.
 */
export interface DestaqueCard {
  key: string;
  title: string;
  description?: string;
  href?: string;
  alt: string;
  isVideo: boolean;
  variant?: DestaqueVariant;
  external?: boolean;
  /**
   * Posição do item em `data.items`. A grade reordena os cards por `variant`,
   * então este é o índice que o lápis precisa passar ao editor — nunca a
   * posição visual.
   */
  sourceIndex: number;
  /** Presente quando a fonte é a curadoria do bloco. */
  curated?: DestaqueItem;
  /** Presente quando a fonte é o fallback automático (`@search`). */
  newsItem?: NewsItem;
}

/** Quantidade de posições da grade: 1 manchete + 1 card alto + 3 cards. */
export const DESTAQUES_COUNT = 5;

/** Formato de cada posição, na ordem em que a grade é preenchida. */
export const DESTAQUES_SLOT_ORDER: DestaqueVariant[] = [
  'hero',
  'tall',
  'card',
  'card',
  'card',
];

/** Escala da imagem por posição — a manchete ocupa 2/3 da coluna. */
export const DESTAQUES_SCALES: Record<DestaqueVariant, string> = {
  hero: 'large',
  tall: 'large',
  card: 'preview',
};

/** Resolve a URL de um link do `object_browser` (interno ou externo). */
export const resolveBrowserHref = (refs?: BrowserRef[]): string | undefined => {
  const first = Array.isArray(refs) ? refs[0] : undefined;
  return first?.['@id'] ? flattenToAppURL(first['@id']) : undefined;
};

/**
 * Texto alternativo herdado do conteúdo referenciado, quando o card usa a
 * imagem dele em vez de arte própria. Vem do `descricaoImagem` da notícia
 * (exposto no summary via `serializers/summary.py`) — nunca do título.
 */
export const inheritedAlt = (refs?: BrowserRef[]): string | undefined => {
  const first = Array.isArray(refs) ? refs[0] : undefined;
  return (first as { descricaoImagem?: string })?.descricaoImagem || undefined;
};

/**
 * Resolve a arte dedicada do item, nos dois formatos que o campo já teve.
 *
 * Widget `image` (atual): grava a URL do conteúdo escolhido/enviado — interna
 * vira `.../@@images/image/{escala}`, externa é usada como está. Widget
 * `object_browser` (itens antigos): grava uma lista de referências.
 */
export const resolveCuratedImage = (
  value: string | BrowserRef[] | undefined,
  scale: string,
): { src: string } | null => {
  if (typeof value === 'string' && value) {
    if (!isInternalURL(value)) return { src: value };
    return { src: flattenToAppURL(`${value}/@@images/image/${scale}`) };
  }
  const picked = Array.isArray(value) ? value[0] : undefined;
  if (!picked?.['@id']) return null;
  const url = getImageURL(picked, scale);
  return url ? { src: flattenToAppURL(url) } : null;
};

/**
 * Props de imagem de um destaque, prontas para o `<Image>` do Volto.
 *
 * Notícia do `@search`: devolve `{ item }` e o próprio `<Image>` monta o
 * `srcset` a partir de `image_scales`.
 *
 * Item curado, em ordem de prioridade:
 *   1. `image` — arte dedicada escolhida pelo editor;
 *   2. `href` — herda a imagem da notícia referenciada;
 *   3. `imageUrl` — arte legada por URL.
 *
 * O degrau 2 é herança viva, não cópia: as URLs de escala do Plone carregam o
 * hash do binário, então congelá-las quebraria o card se a redação trocasse a
 * foto da notícia. Escolher uma arte no campo Imagem desliga a herança.
 */
export const getDestaqueImageProps = (
  card: DestaqueCard,
  scale: string,
): { item: NewsItem } | { src: string } | null => {
  if (card.newsItem) {
    return card.newsItem.image_scales ? { item: card.newsItem } : null;
  }
  const curated = card.curated;
  if (!curated) return null;
  const picked = resolveCuratedImage(curated.image, scale);
  if (picked) return picked;
  const news = Array.isArray(curated.href) ? curated.href[0] : undefined;
  if (news?.image_scales) {
    return { item: news as unknown as NewsItem };
  }
  return curated.imageUrl ? { src: curated.imageUrl } : null;
};

/**
 * Normaliza a fonte da grade num único formato.
 *
 * Havendo curadoria, ela é soberana: 3 itens curados rendem 3 cards, sem
 * completar com notícias — misturar as duas fontes deixaria a redação sem
 * controle sobre o que aparece. Sem curadoria, as últimas notícias preenchem
 * as posições na ordem de `DESTAQUES_SLOT_ORDER`.
 */
export const toDestaqueCards = (
  items: DestaqueItem[] | undefined,
  news: NewsItem[] = [],
): DestaqueCard[] => {
  if (items && items.length > 0) {
    return items.slice(0, DESTAQUES_COUNT).map((item, index) => ({
      key: `curado-${index}`,
      sourceIndex: index,
      title: item.title ?? '',
      description: item.description,
      href: resolveBrowserHref(item.href),
      // Imagem própria → `alt` do editor (obrigatório no schema). Imagem herdada
      // da notícia → texto alternativo da notícia. Nunca o título.
      alt: item.alt || inheritedAlt(item.href) || '',
      isVideo: !!item.isVideo,
      variant: item.variant,
      external: item.external,
      curated: item,
    }));
  }

  return news.slice(0, DESTAQUES_COUNT).map((item, index) => ({
    key: item['@id'] ?? `noticia-${index}`,
    sourceIndex: index,
    title: item.title ?? '',
    description: item.description,
    alt: item.descricaoImagem || '',
    isVideo: false,
    variant: DESTAQUES_SLOT_ORDER[index],
    newsItem: item,
  }));
};

/**
 * Distribui os destaques pelas posições da grade.
 *
 * O `variant` explícito tem prioridade; as posições que sobrarem vazias são
 * preenchidas pelos itens restantes na ordem em que aparecem — a grade nunca
 * fica com buraco por descuido de curadoria.
 */
export const layoutDestaques = (
  cards: DestaqueCard[] = [],
): { hero?: DestaqueCard; tall?: DestaqueCard; cards: DestaqueCard[] } => {
  const pool = cards.slice(0, DESTAQUES_COUNT);
  const hero = pool.find((card) => card.variant === 'hero');
  const tall = pool.find((card) => card !== hero && card.variant === 'tall');
  const queue = pool.filter((card) => card !== hero && card !== tall);

  return {
    hero: hero ?? queue.shift(),
    tall: tall ?? queue.shift(),
    cards: queue.slice(0, 3),
  };
};

/* -----------------------------------------------------------------------
   Faixa "Onde o MPF atua?" (bloco `mpfOndeAtua`)
   ----------------------------------------------------------------------- */

/** Card do carrossel de um tema (widget `object_list`). */
export interface OndeAtuaCardItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  title?: string;
  href?: BrowserRef[];
  external?: boolean;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
  /** URL do YouTube. É a presença dela que liga o selo de play e o modal. */
  videoUrl?: string;
}

/**
 * Dados do bloco `mpfOndeAtua`.
 *
 * Os três temas são slots fixos, gravados achatados (`tema1Label`,
 * `tema1Cards`, ...) e não como lista: `object_list` dentro de `object_list`
 * não é suportado pelo Volto (o `ObjectWidget` não repassa `activeObject`, e o
 * lápis de um card nunca abriria o item certo).
 */
export interface OndeAtuaData {
  title?: string;
  placeholder?: string;
  [key: string]: unknown;
}

/** View-model de um card do carrossel. */
export interface OndeAtuaCard {
  key: string;
  /** Posição do card em `tema{N}Cards` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  title: string;
  href?: string;
  external?: boolean;
  alt: string;
  /** Preenchido só quando `videoUrl` é um endereço de vídeo do YouTube. */
  videoId?: string;
  item: OndeAtuaCardItem;
}

/** View-model de um tema (uma aba). */
export interface OndeAtuaTema {
  /** Número do slot (1..3): entra nos ids de `aria-controls` e no lápis. */
  slot: number;
  label: string;
  subtitulo?: string;
  texto?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaExternal?: boolean;
  cards: OndeAtuaCard[];
}

/** Vídeo em cartaz no modal do player. */
export interface YoutubeVideoRef {
  id: string;
  title?: string;
}

/** Slots fixos de tema, na ordem em que viram abas. */
export const ONDE_ATUA_SLOTS = [1, 2, 3];

/** Escala da imagem do card do carrossel. */
export const ONDE_ATUA_SCALE = 'large';

/**
 * Thumbnail do vídeo no YouTube.
 *
 * `maxresdefault` é 1280x720 sem tarja, mas só existe para vídeos publicados
 * em HD; `hqdefault` existe sempre. Por isso o card pede a primeira e troca
 * para a segunda no `onError` — ver `OndeAtuaCard`.
 */
export const youtubeThumb = (
  videoId: string,
  size: 'maxres' | 'high' = 'maxres',
): string =>
  `https://img.youtube.com/vi/${videoId}/${
    size === 'maxres' ? 'maxresdefault' : 'hqdefault'
  }.jpg`;

const toOndeAtuaCards = (items?: OndeAtuaCardItem[]): OndeAtuaCard[] =>
  (items ?? []).map((item, index) => ({
    key: item['@id'] ?? `card-${index}`,
    sourceIndex: index,
    title: item.title ?? '',
    href: resolveBrowserHref(item.href),
    external: item.external,
    alt: item.alt || inheritedAlt(item.href) || '',
    videoId: item.videoUrl
      ? getYoutubeVideoId(item.videoUrl).videoID ?? undefined
      : undefined,
    item,
  }));

/**
 * Normaliza os campos achatados do bloco nos três temas.
 *
 * Devolve sempre os três slots, mesmo vazios: quem decide o que exibir é a
 * seção (no editor todos aparecem, para o editor enxergar a estrutura; no site
 * só os que têm conteúdo — ver `temaHasContent`).
 */
export const toOndeAtuaTemas = (data?: OndeAtuaData): OndeAtuaTema[] =>
  ONDE_ATUA_SLOTS.map((slot) => ({
    slot,
    label: (data?.[`tema${slot}Label`] as string) ?? '',
    subtitulo: (data?.[`tema${slot}Subtitulo`] as string) ?? undefined,
    texto: (data?.[`tema${slot}Texto`] as string) ?? undefined,
    ctaLabel: (data?.[`tema${slot}CtaLabel`] as string) ?? undefined,
    ctaHref: resolveBrowserHref(data?.[`tema${slot}CtaHref`] as BrowserRef[]),
    ctaExternal: !!data?.[`tema${slot}CtaExternal`],
    cards: toOndeAtuaCards(data?.[`tema${slot}Cards`] as OndeAtuaCardItem[]),
  }));

/** Um tema só vale uma aba no site quando tem algo para mostrar. */
export const temaHasContent = (tema: OndeAtuaTema): boolean =>
  !!(tema.label || tema.texto || tema.subtitulo || tema.cards.length > 0);

/**
 * Se o slot tem algo gravado — qualquer campo, inclusive só o botão.
 *
 * Diferente de `temaHasContent`, que decide a aba no site: aqui a pergunta é
 * se a sidebar pode esconder o slot sem esconder dado do editor.
 */
export const slotHasData = (data: OndeAtuaData | undefined, slot: number) =>
  !!(
    data?.[`tema${slot}Label`] ||
    data?.[`tema${slot}Subtitulo`] ||
    data?.[`tema${slot}Texto`] ||
    data?.[`tema${slot}CtaLabel`] ||
    (data?.[`tema${slot}CtaHref`] as unknown[] | undefined)?.length ||
    (data?.[`tema${slot}Cards`] as unknown[] | undefined)?.length
  );

/**
 * Quantos slots a sidebar mostra no mínimo: até o último com dado (nunca
 * menos que um). É o maior slot, não a contagem — com só o tema 3 preenchido
 * a sidebar mostra os três, para o tema 3 não aparecer como "Tema 2".
 */
export const minVisibleSlots = (data?: OndeAtuaData): number =>
  Math.max(1, ...ONDE_ATUA_SLOTS.filter((slot) => slotHasData(data, slot)));

/**
 * Props de imagem do card, prontas para o `<Image>` do Volto.
 *
 * Em ordem: arte dedicada do campo Imagem (o override manual), thumbnail do
 * vídeo do YouTube e, por fim, a imagem do conteúdo referenciado no link.
 */
export const getOndeAtuaImageProps = (
  card: OndeAtuaCard,
  scale: string = ONDE_ATUA_SCALE,
): { item: NewsItem } | { src: string } | null => {
  const picked = resolveCuratedImage(card.item.image, scale);
  if (picked) return picked;
  if (card.videoId) return { src: youtubeThumb(card.videoId) };
  const ref = Array.isArray(card.item.href) ? card.item.href[0] : undefined;
  return ref?.image_scales ? { item: ref as unknown as NewsItem } : null;
};

/* -----------------------------------------------------------------------
   Cards com véu colorido (faixas `mpfIniciativas` e `mpfDestaquesCarrossel`)
   ----------------------------------------------------------------------- */

/** Rótulo e classes de cor de uma categoria de card. */
export interface OverlayBadge {
  label: string;
  /** Classe do véu sobre a foto. */
  overlay: string;
  /** Classe da pílula, um passo mais claro que o véu. */
  pill: string;
}

/**
 * Categorias do bento "Iniciativas e Projetos".
 *
 * Fonte única: o `choices` do schema e o componente leem daqui, então
 * acrescentar uma categoria é mexer neste objeto e em
 * `_capa-overlay-cards.scss` — em nenhum outro lugar. As cores em si moram no
 * SCSS porque combinam token com opacidade, o que o Tailwind não gera sobre
 * `var(--…)`.
 */
export const INICIATIVA_BADGES = {
  projeto: {
    label: 'Projeto',
    overlay: 'mpf-overlay-card__overlay--projeto',
    pill: 'mpf-overlay-card__badge--projeto',
  },
  campanha: {
    label: 'Campanha',
    overlay: 'mpf-overlay-card__overlay--campanha',
    pill: 'mpf-overlay-card__badge--campanha',
  },
  iniciativa: {
    label: 'Iniciativa',
    overlay: 'mpf-overlay-card__overlay--iniciativa',
    pill: 'mpf-overlay-card__badge--iniciativa',
  },
} as const;

export type IniciativaBadgeKey = keyof typeof INICIATIVA_BADGES;

/** Categoria usada quando o item não escolheu nenhuma. */
export const INICIATIVA_BADGE_DEFAULT: IniciativaBadgeKey = 'projeto';

/** Categoria fixa do carrossel "Destaques" — não é escolhida pelo editor. */
export const MEMORIAL_BADGE: OverlayBadge = {
  label: 'Memorial MPF',
  overlay: 'mpf-overlay-card__overlay--memorial',
  pill: 'mpf-overlay-card__badge--memorial',
};

/** Resolve a categoria do item, caindo no padrão quando o valor é inválido. */
export const getIniciativaBadge = (value?: string): OverlayBadge =>
  INICIATIVA_BADGES[(value ?? '') as IniciativaBadgeKey] ??
  INICIATIVA_BADGES[INICIATIVA_BADGE_DEFAULT];

/* -----------------------------------------------------------------------
   Faixa "Iniciativas e Projetos" (bloco `mpfIniciativas`)
   ----------------------------------------------------------------------- */

/** Posição do card no bento: o primeiro item é o card alto da esquerda. */
export type IniciativaVariant = 'hero' | 'small';

/** Item curado no bloco `mpfIniciativas` (widget `object_list`). */
export interface IniciativaItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  badge?: string;
  title?: string;
  description?: string;
  href?: BrowserRef[];
  external?: boolean;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
}

/** Dados do bloco `mpfIniciativas`. */
export interface IniciativasData {
  title?: string;
  placeholder?: string;
  items?: IniciativaItem[];
}

/** View-model de um card do bento. */
export interface IniciativaCard {
  key: string;
  /** Posição do card em `data.items` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  badge: OverlayBadge;
  title: string;
  description?: string;
  href?: string;
  external?: boolean;
  alt: string;
  item: IniciativaItem;
}

/** Posições do bento: 1 card alto à esquerda + 2 empilhados à direita. */
export const INICIATIVAS_COUNT = 3;

/** Escala da imagem por posição — o card alto ocupa metade da coluna. */
export const INICIATIVAS_SCALES: Record<IniciativaVariant, string> = {
  hero: 'large',
  small: 'preview',
};

/**
 * Normaliza os itens curados no view-model do bento.
 *
 * A ordem da lista é a posição visual (o primeiro item é o card alto), então
 * não há campo de posição nem distribuição a fazer: quem reordena no editor
 * reordena o bento. Excedente é descartado — o design tem três lugares.
 */
export const toIniciativaCards = (items?: IniciativaItem[]): IniciativaCard[] =>
  (items ?? []).slice(0, INICIATIVAS_COUNT).map((item, index) => ({
    key: item['@id'] ?? `iniciativa-${index}`,
    sourceIndex: index,
    badge: getIniciativaBadge(item.badge),
    title: item.title ?? '',
    description: item.description,
    href: resolveBrowserHref(item.href),
    external: item.external,
    // Iniciativas não herda imagem do conteúdo (foto é fundo de véu): a arte é
    // sempre própria, então o `alt` é sempre do editor. Nunca o título.
    alt: item.alt || '',
    item,
  }));

/** Separa o card alto dos dois empilhados. */
export const layoutIniciativas = (
  cards: IniciativaCard[] = [],
): { hero?: IniciativaCard; small: IniciativaCard[] } => ({
  hero: cards[0],
  small: cards.slice(1, INICIATIVAS_COUNT),
});

/**
 * A faixa só existe com curadoria — não há fallback automático como na grade
 * de destaques. Basta um item na lista: card recém-adicionado e ainda vazio
 * precisa aparecer no editor, senão quem edita não enxerga o que criou.
 */
export const iniciativasHasContent = (data?: IniciativasData): boolean =>
  (data?.items?.length ?? 0) > 0;

/**
 * Props de imagem do card, prontas para o `<Image>` do Volto.
 *
 * Só a arte dedicada: diferente dos destaques, o card não herda a imagem do
 * conteúdo referenciado — a foto aqui é fundo de um véu colorido, escolha
 * editorial que a imagem de uma notícia raramente atende.
 */
export const getIniciativaImageProps = (
  card: IniciativaCard,
  variant: IniciativaVariant,
): { src: string } | null =>
  resolveCuratedImage(card.item.image, INICIATIVAS_SCALES[variant]);

/* -----------------------------------------------------------------------
   Faixa "Destaques" (bloco `mpfDestaquesCarrossel`)
   ----------------------------------------------------------------------- */

/**
 * Item curado no bloco `mpfDestaquesCarrossel` (widget `object_list`).
 *
 * Sem campo de categoria: a pílula é sempre "Memorial MPF"
 * (`MEMORIAL_BADGE`), decisão de design e não escolha editorial.
 */
export interface DestaqueCarrosselItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  title?: string;
  description?: string;
  href?: BrowserRef[];
  external?: boolean;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
}

/** Dados do bloco `mpfDestaquesCarrossel`. */
export interface DestaquesCarrosselData {
  title?: string;
  placeholder?: string;
  items?: DestaqueCarrosselItem[];
  /** Chave da barra lateral: troca o destaque sozinho. */
  autoplay?: boolean;
  /** Tempo em cada destaque, em segundos. */
  autoplayDelay?: number;
}

/** View-model de um slide do carrossel. */
export interface DestaqueCarrosselCard {
  key: string;
  /** Posição do slide em `data.items` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  title: string;
  description?: string;
  href?: string;
  external?: boolean;
  alt: string;
  item: DestaqueCarrosselItem;
}

/** Escala da imagem do slide: a coluna é estreita (4/12). */
export const DESTAQUES_CARROSSEL_SCALE = 'preview';

/** Tempo em cada slide, em segundos, com a troca automática ligada. */
export const AUTOPLAY_DELAY_DEFAULT = 8;

/** Piso do intervalo: abaixo disso não dá tempo de ler o card. */
export const AUTOPLAY_DELAY_MIN = 3;

/** Opções de autoplay do Swiper, ou `false` para não ligar o temporizador. */
export type AutoplayConfig =
  | false
  | { delay: number; pauseOnMouseEnter: true; disableOnInteraction: true };

/**
 * Decide se o carrossel passa sozinho.
 *
 * A chave da barra lateral é condição necessária, não suficiente. Também não
 * roda:
 *   - no editor, senão o card muda debaixo do lápis de quem edita;
 *   - com um destaque só, que não tem para onde ir;
 *   - para quem pediu menos animação no sistema.
 *
 * `disableOnInteraction` desliga de vez depois da primeira seta clicada: quem
 * assumiu a navegação não deve ver o conteúdo sair de baixo da leitura.
 */
export const resolveCarrosselAutoplay = ({
  autoplay,
  autoplayDelay,
  isEditMode,
  total,
  prefereMenosMovimento,
}: {
  autoplay?: boolean;
  /** O widget numérico do Volto grava string (`"8"`) — ver a normalização. */
  autoplayDelay?: number | string;
  isEditMode?: boolean;
  total: number;
  prefereMenosMovimento: boolean;
}): AutoplayConfig => {
  if (!autoplay || isEditMode || total < 2 || prefereMenosMovimento) {
    return false;
  }
  // O campo chega do bloco como string, e um `Math.max` direto com valor
  // inválido devolveria `NaN` — o Swiper trocaria de slide sem parar.
  const segundos = Number(autoplayDelay);
  const valido = Number.isFinite(segundos) && segundos > 0;
  return {
    delay:
      Math.max(AUTOPLAY_DELAY_MIN, valido ? segundos : AUTOPLAY_DELAY_DEFAULT) *
      1000,
    pauseOnMouseEnter: true,
    disableOnInteraction: true,
  };
};

/** Normaliza os itens curados no view-model do carrossel. */
export const toDestaqueCarrosselCards = (
  items?: DestaqueCarrosselItem[],
): DestaqueCarrosselCard[] =>
  (items ?? []).map((item, index) => ({
    key: item['@id'] ?? `destaque-carrossel-${index}`,
    sourceIndex: index,
    title: item.title ?? '',
    description: item.description,
    href: resolveBrowserHref(item.href),
    external: item.external,
    // Carrossel não herda imagem do conteúdo (foto é fundo de véu): arte sempre
    // própria, `alt` sempre do editor. Nunca o título.
    alt: item.alt || '',
    item,
  }));

/**
 * A faixa só existe com curadoria. Basta um slide na lista: slide
 * recém-adicionado e ainda vazio precisa aparecer no editor.
 */
export const destaquesCarrosselHasContent = (
  data?: DestaquesCarrosselData,
): boolean => (data?.items?.length ?? 0) > 0;

/** Props de imagem do slide, prontas para o `<Image>` do Volto. */
export const getDestaqueCarrosselImageProps = (
  card: DestaqueCarrosselCard,
  scale: string = DESTAQUES_CARROSSEL_SCALE,
): { src: string } | null => resolveCuratedImage(card.item.image, scale);

/* -----------------------------------------------------------------------
   Faixa "Publicações e Editais" (bloco `mpfPublicacoesEditais`)
   ----------------------------------------------------------------------- */

/**
 * Link de uma aba (widget `object_list`). Sem categoria/imagem: a faixa é
 * só título + destino, em duas colunas.
 */
export interface PublicacaoLinkItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  title?: string;
  href?: BrowserRef[];
  external?: boolean;
}

/**
 * Dados do bloco `mpfPublicacoesEditais`.
 *
 * As quatro abas são slots fixos, gravados achatados (`aba1Label`,
 * `aba1Links`, ...) e não como lista — mesmo motivo do `OndeAtuaData`: um
 * `object_list` dentro de outro não é suportado pelo Volto.
 */
export interface PublicacoesData {
  placeholder?: string;
  [key: string]: unknown;
}

/** View-model de um link, já com o `href` resolvido. */
export interface PublicacaoLinkCard {
  key: string;
  /** Posição do link em `aba{N}Links` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  title: string;
  href?: string;
  external?: boolean;
  item: PublicacaoLinkItem;
}

/** View-model de uma aba. */
export interface PublicacoesAba {
  /** Número do slot (1..4): entra nos ids de `aria-controls` e no lápis. */
  slot: number;
  label: string;
  links: PublicacaoLinkCard[];
}

/** Slots fixos de aba, na ordem em que viram abas. Até 4, por decisão de produto. */
export const PUBLICACOES_SLOTS = [1, 2, 3, 4];

const toPublicacoesLinks = (
  items?: PublicacaoLinkItem[],
): PublicacaoLinkCard[] =>
  (items ?? []).map((item, index) => ({
    key: item['@id'] ?? `link-${index}`,
    sourceIndex: index,
    title: item.title ?? '',
    href: resolveBrowserHref(item.href),
    external: item.external,
    item,
  }));

/**
 * Normaliza os campos achatados do bloco nas quatro abas.
 *
 * Devolve sempre os quatro slots, mesmo vazios: quem decide o que exibir é a
 * seção (no editor todos aparecem; no site só os que têm conteúdo — ver
 * `abaHasContent`).
 */
export const toPublicacoesAbas = (data?: PublicacoesData): PublicacoesAba[] =>
  PUBLICACOES_SLOTS.map((slot) => ({
    slot,
    label: (data?.[`aba${slot}Label`] as string) ?? '',
    links: toPublicacoesLinks(
      data?.[`aba${slot}Links`] as PublicacaoLinkItem[],
    ),
  }));

/** Uma aba só vale a pena mostrar no site quando tem rótulo ou algum link. */
export const abaHasContent = (aba: PublicacoesAba): boolean =>
  !!(aba.label || aba.links.length > 0);

/**
 * Divide a lista de links do painel em duas colunas, metade/metade na ordem
 * — o ímpar sobra para a esquerda. `columns-2` (CSS multi-coluna) daria a
 * régua vertical de graça via `column-rule`, mas não há como suprimir o
 * separador do último item de cada coluna, e o design não quer essa borda
 * sobrando — daí o split manual em duas listas.
 */
export const splitEmColunas = <T>(
  links: T[],
): { esquerda: T[]; direita: T[] } => {
  const meio = Math.ceil(links.length / 2);
  return { esquerda: links.slice(0, meio), direita: links.slice(meio) };
};

/* ------------------------------------------------------------------ *
 * Faixa "Acompanhe nossas redes" (bloco `mpfAcompanheRedes`)
 * ------------------------------------------------------------------ */

/** Os cinco slots de post da grade. */
export const REDES_SLOTS = [1, 2, 3, 4, 5];

/**
 * Slot do card grande da terceira coluna.
 *
 * É estrutural, não uma marcação do editor: a grade 2+2+1 reserva a coluna da
 * direita para vídeo, e é esse card que ganha o selo de play e abre o modal.
 */
export const REDES_VIDEO_SLOT = 5;

/** Dados crus do bloco (campos achatados `card{N}Url`, `perfil{Rede}Url`, ...). */
export interface RedesData {
  placeholder?: string;
  [key: string]: unknown;
}

/** View-model de um card da grade. */
export interface RedesCard {
  /** Número do slot (1..5): entra nas chaves de render e no lápis. */
  slot: number;
  url: string;
  title: string;
  /** Valor cru do campo Capa — resolvido por `resolveCuratedImage`. */
  image?: string;
  /** Texto alternativo da capa. Vazio: a imagem é tratada como decorativa. */
  imageAlt: string;
  /** Token da rede, derivado do domínio da URL. */
  network: SocialNetworkToken;
  /** Id do vídeo no YouTube, quando houver. Liga o selo de play e o modal. */
  videoId?: string;
  /** Verdadeiro no card grande da terceira coluna. */
  isVideoSlot: boolean;
}

/** View-model de um perfil oficial da fileira do cabeçalho. */
export interface RedesPerfil {
  network: SocialGlyphToken;
  url: string;
  label: string;
}

/**
 * Normaliza os campos achatados do bloco nos cinco cards.
 *
 * Devolve sempre os cinco slots, mesmo vazios: quem decide o que exibir é a
 * seção (no editor todos aparecem, para haver onde clicar; no site só os que
 * têm URL — ver `redesCardHasContent`).
 *
 * A rede sai do domínio da URL a cada chamada, e não de um campo gravado: são
 * cinco regex, e derivar elimina a chance de o ícone ficar dessincronizado da
 * URL depois de uma troca de link.
 */
export const toRedesCards = (data?: RedesData): RedesCard[] =>
  REDES_SLOTS.map((slot) => {
    const url = ((data?.[`card${slot}Url`] as string) ?? '').trim();
    const network = resolveSocialNetwork(url);
    return {
      slot,
      url,
      title: ((data?.[`card${slot}Title`] as string) ?? '').trim(),
      image: data?.[`card${slot}Image`] as string | undefined,
      imageAlt: ((data?.[`card${slot}ImageAlt`] as string) ?? '').trim(),
      network,
      videoId:
        network === 'youtube'
          ? getYoutubeVideoId(url)?.videoID || undefined
          : undefined,
      isVideoSlot: slot === REDES_VIDEO_SLOT,
    };
  });

/** Um card só vale a pena mostrar no site quando tem para onde apontar. */
export const redesCardHasContent = (card: RedesCard): boolean => !!card.url;

/**
 * Id do campo de perfil de cada rede do cabeçalho.
 *
 * Mora aqui, e não no schema do bloco, porque os dois lados precisam dele e o
 * schema já importa deste módulo — o caminho inverso fecharia um ciclo.
 */
export const perfilFieldId = (network: string): string =>
  `perfil${network.charAt(0).toUpperCase()}${network.slice(1)}Url`;

/** Perfis preenchidos, na ordem da fileira do design. Campo vazio some. */
export const toRedesPerfis = (data?: RedesData): RedesPerfil[] =>
  SOCIAL_ORDER.reduce<RedesPerfil[]>((acc, network) => {
    const url = ((data?.[perfilFieldId(network)] as string) ?? '').trim();
    if (url) acc.push({ network, url, label: SOCIAL_LABELS[network] });
    return acc;
  }, []);

/** A faixa some do site quando não há nem post nem perfil cadastrado. */
export const redesHasContent = (data?: RedesData): boolean =>
  toRedesCards(data).some(redesCardHasContent) ||
  toRedesPerfis(data).length > 0;

/**
 * Imagem do card, em ordem de prioridade:
 *   1. `card{N}Image` — capa escolhida pelo editor (ou trazida pela busca);
 *   2. miniatura do YouTube derivada do id do vídeo, sem chamada de rede.
 *
 * O degrau 2 é o que faz o card de vídeo funcionar mesmo com o serviço de
 * metadados fora do ar — ver `youtubeThumb`.
 */
export const getRedesCardImage = (
  card: RedesCard,
  scale: string,
): { src: string } | null => {
  const curated = resolveCuratedImage(card.image, scale);
  if (curated) return curated;
  return card.videoId ? { src: youtubeThumb(card.videoId) } : null;
};
